import { useState, useEffect, useMemo } from 'react';
import client from '../../api/client';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import UpgradeModal from '../upgrade/UpgradeModal';

const SPORTS = [
  { key: 'basketball_nba', label: 'NBA' },
  { key: 'baseball_mlb', label: 'MLB' },
  { key: 'americanfootball_nfl', label: 'NFL' },
  { key: 'icehockey_nhl', label: 'NHL' },
];

function friendlyPropLabel(label) {
  if (!label) return '';
  // Already friendly (contains space or "O/U")
  if (label.includes(' ')) return label;
  // Strip prefixes and title-case
  let s = label;
  for (const prefix of ['player_', 'batter_', 'pitcher_']) {
    if (s.startsWith(prefix)) { s = s.slice(prefix.length); break; }
  }
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const CONFIDENCE_STYLES = {
  high: 'bg-[rgba(34,197,94,0.1)] text-[#22c55e]',
  moderate: 'bg-[rgba(245,158,11,0.1)] text-[#f59e0b]',
  low: 'bg-[rgba(148,163,184,0.1)] text-[#94a3b8]',
};

function fmtOdds(n) {
  if (n == null) return '--';
  return n > 0 ? `+${n}` : `${n}`;
}

export default function PropPicks() {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSport, setActiveSport] = useState(null);
  const tier = useEffectiveTier();
  const [modalOpen, setModalOpen] = useState(false);
  const isLocked = tier === 'free';

  useEffect(() => {
    setLoading(true); setError(null);
    client.get('/api/props/picks')
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : [];
        setPicks(data);
        // Auto-select first sport that has picks
        const sportSet = new Set(data.map((p) => p.sport_key));
        const first = SPORTS.find((s) => sportSet.has(s.key));
        if (first) setActiveSport(first.key);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const sportCounts = useMemo(() => {
    const counts = {};
    for (const p of picks) {
      counts[p.sport_key] = (counts[p.sport_key] || 0) + 1;
    }
    return counts;
  }, [picks]);

  const activeSports = SPORTS.filter((s) => sportCounts[s.key]);
  const filtered = activeSport ? picks.filter((p) => p.sport_key === activeSport) : picks;
  const generatedAt = picks[0]?.generated_at;

  return (
    <div className="px-5 py-6">
      {/* Header */}
      <div className="mb-1">
        <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>AI Picks</p>
      </div>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Prop Picks</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>AI-curated player prop recommendations based on odds analysis. Updated daily.</p>

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-4">
          <p className="text-[#ef4444] text-[11px]">Failed to load: {error}</p>
        </div>
      )}

      {loading ? (
        <div className="max-w-[1100px] space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}</div>
      ) : picks.length === 0 ? (
        <div className="max-w-[1100px]">
          <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-6 py-12 text-center">
            <p className="text-[16px] text-[#6B7280] dark:text-[#8892a8] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>No prop picks for today yet</p>
            <p className="text-[14px] text-[#9CA3AF] dark:text-[#505a6e]" style={{ fontFamily: 'var(--font-sans)' }}>Picks are generated daily when games with prop markets are available. Check back later.</p>
          </div>
        </div>
      ) : (
        <div className="max-w-[1100px]">
          {/* Sport tabs */}
          {activeSports.length > 1 && (
            <div className="flex items-end gap-0 mb-4 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
              {activeSports.map((s) => (
                <button key={s.key} onClick={() => setActiveSport(s.key)}
                  className={`px-0 mr-6 pb-2 text-[14px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
                    activeSport === s.key
                      ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                      : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
                  }`}
                  style={{ fontFamily: 'var(--font-sans)' }}>
                  {s.label}
                  <span className="text-[12px] text-[#9CA3AF] dark:text-[#505a6e] ml-1.5">{sportCounts[s.key]}</span>
                </button>
              ))}
            </div>
          )}

          {/* Pick cards — 2-col on larger screens */}
          {isLocked ? (
            <div className="relative">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 select-none pointer-events-none" style={{ filter: 'blur(8px)' }}>
                {filtered.map((pick, i) => <PickCard key={pick.id || i} pick={pick} />)}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="rounded-lg border border-[#cbd5e1] dark:border-[#2d3748] bg-white/95 dark:bg-[#151a25]/95 shadow-lg px-6 py-6 text-center max-w-sm">
                  <svg className="w-8 h-8 text-[#94a3b8] dark:text-[#505a6e] mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                  <p className="text-[15px] font-semibold text-[#111827] dark:text-[#e2e8f0] mb-1" style={{ fontFamily: 'var(--font-sans)' }}>
                    {filtered.length} pick{filtered.length !== 1 ? 's' : ''} available today
                  </p>
                  <p className="text-[13px] text-[#4b5563] dark:text-[#94a3b8] mb-4 leading-relaxed" style={{ fontFamily: 'var(--font-sans)' }}>
                    Unlock AI-curated prop picks with player-level analysis and bookmaker odds comparison.
                  </p>
                  <button onClick={() => setModalOpen(true)}
                    className="rounded-md bg-[#005BB3] px-5 py-2 text-[14px] font-medium text-white hover:bg-[#004a93] transition-colors duration-150"
                    style={{ fontFamily: 'var(--font-sans)' }}>
                    Upgrade to Pro
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {filtered.map((pick, i) => <PickCard key={pick.id || i} pick={pick} />)}
            </div>
          )}

          <UpgradeModal open={modalOpen} onClose={() => setModalOpen(false)} suggestedTier="pro" triggerFeature="prop_picks" />

          {/* Footer info */}
          <div className="mt-6 rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] bg-[#F8F9FA] dark:bg-[#0f1219] px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-[13px] text-[#6B7280] dark:text-[#8892a8] leading-relaxed" style={{ fontFamily: 'var(--font-sans)' }}>
                  Picks are generated daily by AI analysis of player prop odds across multiple bookmakers. The model identifies lines where bookmaker consensus diverges, then evaluates recent player performance and matchup context to surface the strongest edges.
                </p>
                {generatedAt && (
                  <p className="text-[11px] text-[#9CA3AF] dark:text-[#505a6e] mt-2" style={{ fontFamily: 'var(--font-mono)' }}>
                    Last updated: {new Date(generatedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function PickCard({ pick }) {
  const odds = pick.odds_snapshot || [];
  const confStyle = CONFIDENCE_STYLES[pick.confidence] || CONFIDENCE_STYLES.low;
  const isOver = pick.recommendation === 'over';

  return (
    <div className="rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] border-l-[3px] border-l-[#005BB3] dark:border-l-[#3b82f6] bg-white dark:bg-[#151a25] px-4 py-3">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[15px] font-semibold text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>{pick.player_name}</span>
            {pick.player_team && <span className="text-[12px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>{pick.player_team}</span>}
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${confStyle}`}>{(pick.confidence || 'low').toUpperCase()}</span>
          </div>
          <p className="text-[13px] text-[#6B7280] dark:text-[#8892a8] mt-0.5" style={{ fontFamily: 'var(--font-sans)' }}>
            {pick.away_team} @ {pick.home_team}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>{friendlyPropLabel(pick.prop_label || pick.prop_type)}</p>
          <p className="text-[18px] font-bold text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>
            {isOver ? 'Over' : 'Under'} {pick.line}
          </p>
        </div>
      </div>

      {/* Odds by bookmaker */}
      {odds.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {odds.map((o, i) => (
            <div key={i} className="rounded bg-[#F8F9FA] dark:bg-[#0f1219] px-2 py-1 text-center">
              <p className="text-[10px] text-[#9CA3AF] dark:text-[#505a6e] truncate" style={{ fontFamily: 'var(--font-sans)' }}>{o.bookmaker}</p>
              <div className="flex gap-2 mt-0.5">
                <span className={`text-[12px] font-medium ${isOver ? 'text-[#029456]' : 'text-[#6B7280] dark:text-[#8892a8]'}`} style={{ fontFamily: 'var(--font-mono)' }}>
                  O {fmtOdds(o.over_price)}
                </span>
                <span className={`text-[12px] font-medium ${!isOver ? 'text-[#029456]' : 'text-[#6B7280] dark:text-[#8892a8]'}`} style={{ fontFamily: 'var(--font-mono)' }}>
                  U {fmtOdds(o.under_price)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI explanation */}
      {pick.explanation && (
        <p className="text-[13px] text-[#1F2937] dark:text-[#8892a8] leading-relaxed" style={{ fontFamily: 'var(--font-sans)' }}>{pick.explanation}</p>
      )}
    </div>
  );
}
